import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Container, Form, Button, Alert, Card } from "react-bootstrap";
import { sendWelcomeEmail } from "../services/emailNotifications";
import { normalizeRole } from "../utils/roles";
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(""); // Estado para el rol (donante o solicitante)
  const [error, setError] = useState("");
  const [isRegistering, setIsRegistering] = useState(false); // Estado para alternar entre registro e inicio de sesión
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Usuario inició sesión:", user);
  
      // Obtener el rol del usuario desde Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userRole = normalizeRole(userDoc.data().role);
  
        // Redirigir según el rol
        if (userRole === "donor") {
          navigate("/add-medication");
        } else if (userRole === "requester") {
          navigate("/home");
        }
      } else {
        setError("No se encontró el rol del usuario.");
      }
    } catch (error) {
      console.error("Error al iniciar sesión:", error.message);
      if (error.code === "auth/user-not-found") {
        setError("El usuario no está registrado.");
      } else if (error.code === "auth/wrong-password") {
        setError("La contraseña es incorrecta.");
      } else if (error.code === "auth/invalid-email") {
        setError("El correo electrónico no es válido.");
      } else {
        setError("Error al iniciar sesión: " + error.message);
      }
    }
  };

  // Función para manejar el registro
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!role) {
      setError("Por favor, selecciona un rol (donante o solicitante).");
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Usuario registrado:", user);

      // Guardar el rol en Firestore
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        role: role,
      });

      await sendWelcomeEmail({
        to: user.email,
        name: user.displayName || user.email?.split("@")?.[0],
        role,
      });

      // Redirigir según el rol
      if (role === "donor") {
        navigate("/add-medication");
      } else if (role === "requester") {
        navigate("/home");
      }
    } catch (error) {
      console.error("Error al registrar:", error.message);
      setError("Error al registrar: " + error.message);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
      <Card style={{ width: "100%", maxWidth: "400px", padding: "20px" }}>
        <Card.Body>
          <h2 className="text-center mb-4">{isRegistering ? "Registro" : "Iniciar Sesión"}</h2>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={isRegistering ? handleRegister : handleLogin}>
            {/* Campo de correo electrónico */}
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control
                type="email"
                placeholder="Ingresa tu correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            {/* Campo de contraseña */}
            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Contraseña</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            {/* Selector de rol (solo visible en el registro) */}
            {isRegistering && (
              <Form.Group className="mb-3" controlId="formRole">
                <Form.Label>Selecciona tu rol:</Form.Label>
                <Form.Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  required
                >
                  <option value="">Selecciona una opción</option>
                  <option value="donor">Donante</option>
                  <option value="requester">Solicitante</option>
                </Form.Select>
              </Form.Group>
            )}

            {/* Botón de registro o inicio de sesión */}
            <Button variant="primary" type="submit" className="w-100 mb-3">
              {isRegistering ? "Registrarse" : "Iniciar Sesión"}
            </Button>

            {/* Enlace para alternar entre registro e inicio de sesión */}
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => setIsRegistering(!isRegistering)}
              >
                {isRegistering
                  ? "¿Ya tienes una cuenta? Inicia sesión"
                  : "¿No tienes una cuenta? Regístrate"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Login;
